import classNames from 'classnames'
import {
  createContext,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type CascadingMenuOption } from '@opallabs/concepts'
import * as styles from '../styles'

export interface CascadingMenuProps {
  className?: string
  initialSelection?: CascadingMenuOption
  /**
   * If provided and the item has `childOptions`, the menu mounts already descended into that submenu (with a back arrow
   * to return to root). The item itself must come from `items` so navigation history is consistent.
   */
  initialDescendInto?: CascadingMenuOption
  /**
   * Used to enable keyboard navigation from menu items to the footer. Must be true
   * when InteractiveFooter is used.
   */
  interactiveFooter?: boolean
  items: Array<CascadingMenuOption>
  onActiveDescendentChange?: (id: string) => void
  onCloseMenu?: () => void
  onSelect: (item: CascadingMenuOption) => void
  rootHeader?: string
}

type CascadingMenuContextValue = Pick<
  CascadingMenuProps,
  | 'items'
  | 'interactiveFooter'
  | 'onCloseMenu'
  | 'onSelect'
  | 'onActiveDescendentChange'
  | 'rootHeader'
> & {
  focusedItemIndex: number
  searchResults: CascadingMenuOption[]
  searchValue: string
  selectedItem: CascadingMenuOption | null
  submenuTitle: string | null
  setFocusedItemIndex: (value: number) => void
  setSearchResults: (value: CascadingMenuOption[]) => void
  setSearchValue: (value: string) => void
  setSelectedItem(item: CascadingMenuOption | null): void
  /** 1-based index to indicate menu depth */
  stackLevel: number
  ascendMenuLevel: () => void
  descendMenuLevel: (item: CascadingMenuOption) => void
  setActiveList: (items: Array<CascadingMenuOption>) => void
}

const CascadingMenuContext = createContext<
  CascadingMenuContextValue | undefined
>(undefined)
CascadingMenuContext.displayName =
  'Lapidary: CascadingMenu.CascadingMenuContext'

export const Root = forwardRef<
  HTMLDivElement,
  PropsWithChildren<CascadingMenuProps>
>(
  (
    {
      className,
      initialSelection,
      initialDescendInto,
      interactiveFooter,
      items,
      onCloseMenu,
      onActiveDescendentChange,
      onSelect,
      children,
      rootHeader,
    },
    ref
  ) => {
    // Track the index of the parent menu to refocus it when ascending
    const prevFocusedIndexList = useRef<number[]>([])
    const prevItems = useRef<CascadingMenuOption[]>(flattenItems(items))
    const initialDescent = useMemo(
      () => (initialDescendInto?.childOptions ? initialDescendInto : null),
      [initialDescendInto]
    )

    const [activeList, setActiveList] = useState<Array<CascadingMenuOption>>(
      initialDescent?.childOptions ?? items
    )
    const [searchValue, setSearchValue] = useState('')
    const [searchResults, setSearchResults] = useState(activeList)

    const [parentLevels, setParentLevels] = useState<
      Array<Array<CascadingMenuOption>>
    >(initialDescent ? [items] : [])
    const [headerStack, setHeaderStack] = useState<Array<CascadingMenuOption>>(
      initialDescent ? [initialDescent] : []
    )
    const [selectedItem, setSelectedItem] =
      useState<CascadingMenuOption | null>(initialSelection ?? null)
    // Start with search input focused
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1)

    const resetConnectedStateValues = useCallback(
      (activeList: CascadingMenuOption[]) => {
        setActiveList(activeList)
        setSearchValue('')
        setSearchResults(activeList)
      },
      []
    )

    // If the items list changed because of an action
    // taken in the footer, keep focus on the footer
    // after the new items populate
    const preserveFooterFocus = useCallback(
      (nextActiveItems: CascadingMenuOption[]) => {
        if (!interactiveFooter) return

        const searchCount = searchResults.length ?? 1
        if (focusedItemIndex === searchCount) {
          setFocusedItemIndex(nextActiveItems.length)
        }
      },
      [focusedItemIndex, interactiveFooter, searchResults]
    )

    const descendMenuLevel = (item: CascadingMenuOption) => {
      prevFocusedIndexList.current.push(focusedItemIndex)
      setHeaderStack((stack) => [...stack, item])
      setParentLevels((stack) => [activeList, ...stack])
      resetConnectedStateValues(item.childOptions ?? [])

      // Per a11y guidelines we'll focus the first option, not the search input
      setFocusedItemIndex(0)
    }

    const ascendMenuLevel = () => {
      setHeaderStack((stack) => stack.slice(0, stack.length - 1))
      const [items, ...parentStack] = parentLevels
      setParentLevels(parentStack)
      resetConnectedStateValues(items)

      const prevIndex = prevFocusedIndexList.current.pop()
      setFocusedItemIndex(prevIndex ?? 0)
    }

    const currentItem = headerStack.at(-1)
    const headerTitle = currentItem?.headerLabel ?? currentItem?.label ?? ''

    // Update menu state if items props change. This should be done
    // deliberately because this also resets the menu to its highest level.
    // Currently used by BlockConnectionsField to show/hide blocks that
    // match the "in support of" category type for a specific block category type
    useEffect(() => {
      const nextFlattenedItems = flattenItems(items)
      if (areItemsEqual(prevItems.current, nextFlattenedItems)) return

      if (initialDescent) {
        preserveFooterFocus(initialDescent.childOptions ?? [])
        resetConnectedStateValues(initialDescent.childOptions ?? [])
        setParentLevels([items])
        setHeaderStack([initialDescent])
      } else {
        preserveFooterFocus(items)
        resetConnectedStateValues(items)
        setParentLevels([])
        setHeaderStack([])
      }

      prevItems.current = nextFlattenedItems
    }, [items, initialDescent, resetConnectedStateValues, preserveFooterFocus])

    return (
      <CascadingMenuContext.Provider
        value={{
          ascendMenuLevel,
          descendMenuLevel,
          focusedItemIndex,
          interactiveFooter,
          items: activeList,
          onActiveDescendentChange,
          onCloseMenu,
          onSelect,
          rootHeader,
          searchValue,
          searchResults,
          selectedItem,
          setActiveList,
          setFocusedItemIndex,
          setSearchResults,
          setSearchValue,
          setSelectedItem,
          stackLevel: headerStack.length + 1,
          submenuTitle: headerStack.length > 0 ? headerTitle : null,
        }}
      >
        {/* @see https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/8f75961d965e47afb88854d324bd32fafde7acfe/docs/rules/no-noninteractive-element-interactions.md#case-this-element-is-catching-bubbled-events-from-elements-that-it-contains */}
        <div role="presentation" onClick={(e) => e.stopPropagation()}>
          <div
            className={classNames(styles.selectors.root, className)}
            css={styles.container}
            ref={ref}
          >
            {children}
          </div>
        </div>
      </CascadingMenuContext.Provider>
    )
  }
)
Root.displayName = 'Lapidary: CascadingMenu.Root'

export const useCascadingMenu = () => {
  const context = useContext(CascadingMenuContext)

  if (!context) {
    throw new Error(
      'CascadingMenu components must be used within CascadingMenu.Root'
    )
  }

  return context
}

export function flattenItems(
  items: Array<CascadingMenuOption>
): Array<CascadingMenuOption> {
  return items.flatMap((item) =>
    item.childOptions?.length
      ? [item, ...flattenItems(item.childOptions)]
      : [item]
  )
}

export function areItemsEqual(
  items: Array<CascadingMenuOption>,
  nextItems: Array<CascadingMenuOption>
) {
  // Quick exit if we don't need to check individual item ids
  if (items.length !== nextItems.length) return false

  return (
    items.map(({ value }) => value).join() ===
    nextItems.map(({ value }) => value).join()
  )
}
